/**
 * Central manifest for all routes in the application
 */
export enum Routes {
    home = "/",
    notFound = "/404",
    overview = "/overview",
    citations = "/citations",
    login = "/login",
    privacyPolicy = "/privacy-policy",
    about = "/about",
    termsOfUse = "/terms-of-use",
    register = "/register",
    authError = "/auth/error",
    authVerify = "/auth/verify",
    treasuries = "/treasuries",
    governmentBonds = "/government-bonds",
    settings = "/settings",
    settingsProfile = "/settings/profile",
    settingsPlan = "/settings/plan",
    settingsOrganization = "/settings/team",
    developerVisualization = "/developer/visualization",
    developerEmbeds = "/developer/embeds",
    // // // //
    // Stripe Payments + Checkout
    payment_success = "/billing/payments/success",
    // // // //
    salesPage = "/platform-overview",
    news = "/news",
    blog = "/blog",
    blogIndividual = "/blog/[slug]",
    newAssetMonitor = "/new-asset-monitor",
    assetScreener = "/asset-screener",
    assetComparison = "/asset-comparison",
    assetClasses = "/asset-classes",
    // // // //
    // Tools
    assetCharting = "/tools/asset-charting",
    flowsExplorer = "/tools/flows-explorer",
    charts = "/charts",
    chartNew = "/charts/new",
    chartIndividual = "/charts/[slug]",
    chartEdit = "/charts/[slug]/edit",
    // // // //
    // Explore
    exploreNetFlows = "/charts/explore/net-flows",
    exploreTimeseries = "/charts/explore/timeseries",
    exploreComposition = "/charts/explore/composition",
    exploreLeagueTable = "/charts/explore/league-table",
    exploreAssetTable = "/charts/explore/asset-table",
    // // // //
    search = "/search",
    // // // //
    // MCP Tools + OAuth Clients (for connecting MCP clients)
    toolsMcp = "/tools/mcp",
    oauthClients = "/tools/mcp/settings/oauth",
    oauthClientsNew = "/tools/mcp/settings/oauth/new",
    oauthClientDetail = "/tools/mcp/settings/oauth/[clientId]",

    // API Tools
    apiTools = "/tools/api",
    apiToolsApiKeys = "/tools/api/api-keys",
    // API Tools - Endpoints - V4
    apiToolsV4Assets = "/tools/api/endpoints/v4/assets",
    apiToolsV4Tokens = "/tools/api/endpoints/v4/tokens",
    apiToolsV4TokenHolders = "/tools/api/endpoints/v4/token_holders",
    apiToolsV4TokenTimeseries = "/tools/api/endpoints/v4/token_timeseries",
    apiToolsV4AssetTimeseries = "/tools/api/endpoints/v4/asset_timeseries",
    apiToolsV4Networks = "/tools/api/endpoints/v4/networks",
    apiToolsV4Platforms = "/tools/api/endpoints/v4/platforms",
    apiToolsV4Protocols = "/tools/api/endpoints/v4/protocols",
    apiToolsV4Issuers = "/tools/api/endpoints/v4/issuers",
    apiToolsV4ServiceProviders = "/tools/api/endpoints/v4/service_providers",
    apiToolsV4AssetClasses = "/tools/api/endpoints/v4/asset_classes",
    apiToolsV4Managers = "/tools/api/endpoints/v4/managers",
    apiToolsV4Transactions = "/tools/api/endpoints/v4/transactions",
    apiToolsV4TokenAggregates = "/tools/api/endpoints/v4/token_aggregates",
    apiToolsV4AssetAggregates = "/tools/api/endpoints/v4/asset_aggregates",
    apiToolsV4Measures = "/tools/api/endpoints/v4/measures",
    apiToolsV4ReferenceData = "/tools/api/endpoints/v4/reference_data",
    // // // //
    // Partners Pages
    broadridge = "/broadridge-dlr",
    // // // //
    platforms = "/platforms",
    platformIndividual = "/platforms/[platformSlug]",
    transactions = "/transactions",
    assets = "/assets",
    assetIndividual = "/assets/[assetSlug]",
    assetTimeseries = "/assets/timeseries",
    assetAggregates = "/assets/aggregates",
    stablecoins = "/stablecoins",
    activeStrategies = "/active-strategies",
    tokens = "/tokens",
    tokenTimeseries = "/tokens/timeseries",
    tokenAggregates = "/tokens/aggregates",
    tokenHolders = "/tokens/holders",
    commodities = "/commodities",
    stocks = "/stocks",
    trade = "/trade",
    privateEquity = "/private-equity-venture-capital",
    credit = "/credit",
    realEstate = "/real-estate",
    networks = "/networks",
    networkIndividual = "/networks/[networkSlug]",
    managers = "/asset-managers",
    managerIndividual = "/asset-managers/[managerSlug]",
    serviceProviders = "/service-providers",
    serviceProviderIndividual = "/service-providers/[serviceProviderSlug]",
    // // // //
    // Data Catalog
    catalog = "/catalog",
    measures = "/catalog/metrics",
    measureIndividual = "/catalog/metrics/[metricSlug]",
    referenceData = "/catalog/reference",
    referenceDataIndividual = "/catalog/reference/[referenceSlug]",
    // // // //
    // Admin - Users
    userListing = "/admin/users",
    userIndividual = "/admin/users/[userID]",
    // // // //
    // Admin - API Keys
    apiKeyListing = "/admin/api_keys",
    // // // //
    // Admin - OAuth Clients
    oauthClientListing = "/admin/oauth_clients",
    // // // //
    // Admin - Organizations
    organizationListing = "/admin/organizations",
    organizationIndividual = "/admin/organizations/[organizationID]",
    // // // //
    // Admin - Asset Chats
    assetChatListing = "/admin/asset_chats",
    assetChatIndividual = "/admin/asset_chats/[assetChatID]",
    // // // //
    // Admin - Charts
    adminChartListing = "/admin/charts",
    // // // //
    // Admin - Developer
    adminDeveloper = "/admin/developer",
    visibility = "/admin/visibility",
    // // // //
    // Admin - API Access - User Roles
    apiAccessUserRoleListing = "/admin/api_access_user_roles",
    apiAccessUserRoleNew = "/admin/api_access_user_roles/new",
    apiAccessUserRoleEdit = "/admin/api_access_user_roles/[apiAccessUserRoleID]/edit",
    // // // //
    // Admin - API Access - Org Roles
    apiAccessOrgRoleListing = "/admin/api_access_org_roles",
    apiAccessOrgRoleNew = "/admin/api_access_org_roles/new",
    apiAccessOrgRoleEdit = "/admin/api_access_org_roles/[apiAccessOrgRoleID]/edit",
    // // // //
    // Admin - API Access - Policy Assignments
    apiAccessPolicyAssignmentListing = "/admin/api_access_policy_assignments",
    apiAccessPolicyAssignmentNew = "/admin/api_access_policy_assignments/new",
    apiAccessPolicyAssignmentIndividual = "/admin/api_access_policy_assignments/[apiAccessPolicyAssignmentID]",
    apiAccessPolicyAssignmentEdit = "/admin/api_access_policy_assignments/[apiAccessPolicyAssignmentID]/edit",
    // // // //
    // Admin - API Access - Roles
    apiAccessRoleListing = "/admin/api_access_roles",
    apiAccessRoleNew = "/admin/api_access_roles/new",
    apiAccessRoleIndividual = "/admin/api_access_roles/[apiAccessRoleID]",
    apiAccessRoleEdit = "/admin/api_access_roles/[apiAccessRoleID]/edit",
    // // // //
    // Admin - API Access - Policies
    apiAccessPolicyListing = "/admin/api_access_policies",
    apiAccessPolicyNew = "/admin/api_access_policies/new",
    apiAccessPolicyIndividual = "/admin/api_access_policies/[apiAccessPolicyID]",
    apiAccessPolicyEdit = "/admin/api_access_policies/[apiAccessPolicyID]/edit",
}

// // // //

// Get the URL for /networks/[networkSlug] page
export function getNetworkURL({ networkSlug }: { networkSlug: string }) {
    return `/networks/${networkSlug}`;
}

// Get the URL for /platforms/[platformSlug] page
export function getPlatformURL({ platformSlug }: { platformSlug: string }) {
    if (platformSlug === "broadridge-dlr") {
        return Routes.broadridge;
    }
    return `/platforms/${platformSlug}`;
}

export function getManagerURL({ managerSlug }: { managerSlug: string }) {
    return `/asset-managers/${managerSlug}`;
}

export function getServiceProviderURL({
    serviceProviderSlug,
}: {
    serviceProviderSlug: string;
}) {
    return `/service-providers/${serviceProviderSlug}`;
}

export function getMeasureURL({ measureSlug }: { measureSlug: string }) {
    return `/catalog/metrics/${measureSlug}`;
}

export function getReferenceDataURL({
    referenceSlug,
}: {
    referenceSlug: string;
}) {
    return `/catalog/reference/${referenceSlug}`;
}

export function getBlogPostURL({ slug }: { slug: string }) {
    return `${Routes.blog}/${slug}`;
}

// Get the URL for /charts/[slug] page
export function getChartURL({ slug }: { slug: string }) {
    return `/charts/${slug}`;
}

// Get the URL for /charts/[slug]/edit page
export function getChartEditURL({ slug }: { slug: string }) {
    return `/charts/${slug}/edit`;
}

// Get the URL for /tools/mcp/settings/oauth/[clientId] page
export function getOauthClientDetailURL({ clientId }: { clientId: string }) {
    return `${Routes.oauthClients}/${clientId}`;
}
